import api from './client';
import type { MusikkKategori, Musikk, MusikkTransponering, MusikkFil } from './types';

/**
 * Musikk API
 * Håndterer musikkbibliotek, kategorier og transponeringer
 */
export const musikkAPI = {
  // ===== KATEGORIER =====
  
  getAllKategorier: async (): Promise<{ kategorier: MusikkKategori[] }> => {
    const response = await api.get('/musikk/kategorier');
    return response.data;
  },

  createKategori: async (data: { 
    navn: string; 
    beskrivelse?: string 
  }): Promise<{ kategori: MusikkKategori }> => {
    const response = await api.post('/musikk/kategorier', data);
    return response.data;
  },

  updateKategori: async (id: number, data: { 
    navn: string; 
    beskrivelse?: string 
  }): Promise<{ kategori: MusikkKategori }> => {
    const response = await api.put(`/musikk/kategorier/${id}`, data);
    return response.data;
  },

  deleteKategori: async (id: number): Promise<void> => {
    await api.delete(`/musikk/kategorier/${id}`);
  },

  // ===== MUSIKK =====
  
  getAll: async (filters?: { 
    publisert?: boolean; 
    kategori_id?: number; 
    sok?: string 
  }): Promise<{ musikk: Musikk[] }> => {
    const response = await api.get('/musikk', { params: filters });
    return response.data;
  },

  getById: async (id: number): Promise<{ musikk: Musikk }> => {
    const response = await api.get(`/musikk/${id}`);
    return response.data;
  },

  create: async (data: {
    tittel: string;
    artist?: string;
    album?: string;
    utgivelsesaar?: number;
    publisert?: boolean;
    kategori_ids?: number[];
    notater?: string;
    lengde_minutter?: number;
    lengde_sekunder?: number;
    grunntone?: string;
    toneart_type?: string;
    bpm?: number;
    tekst_og_akkorder?: string;
  }): Promise<{ musikk: Musikk }> => {
    const response = await api.post('/musikk', data);
    return response.data;
  },

  update: async (id: number, data: {
    tittel: string;
    artist?: string;
    album?: string;
    utgivelsesaar?: number;
    publisert?: boolean;
    kategori_ids?: number[];
    notater?: string;
    lengde_minutter?: number;
    lengde_sekunder?: number;
    grunntone?: string;
    toneart_type?: string;
    bpm?: number;
    tekst_og_akkorder?: string;
  }): Promise<{ musikk: Musikk }> => {
    const response = await api.put(`/musikk/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/musikk/${id}`);
  },

  // ===== TRANSPONERINGER =====
  
  getAllTransponeringer: async (musikkId: number): Promise<{ transponeringer: MusikkTransponering[] }> => {
    const response = await api.get(`/musikk/${musikkId}/transponeringer`);
    return response.data;
  },

  createTransponering: async (musikkId: number, data: {
    navn: string;
    grunntone: string;
    toneart_type: string;
    semitoner: number;
  }): Promise<{ transponering: MusikkTransponering }> => {
    const response = await api.post(`/musikk/${musikkId}/transponeringer`, data);
    return response.data;
  },

  deleteTransponering: async (transponeringId: number): Promise<void> => {
    await api.delete(`/musikk/transponeringer/${transponeringId}`);
  },

  // ===== FILER =====
  
  getFiles: async (musikkId: number): Promise<{ filer: MusikkFil[] }> => {
    const response = await api.get(`/musikk/${musikkId}/filer`);
    return response.data;
  },

  uploadFile: async (musikkId: number, file: File, navn: string): Promise<{ fil: MusikkFil }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('navn', navn);
    formData.append('filType', 'upload');

    // Interceptor håndterer Content-Type header automatisk for FormData
    const response = await api.post(`/musikk/${musikkId}/filer`, formData);
    return response.data;
  },

  createUrlFile: async (musikkId: number, navn: string, filType: 'url', url: string): Promise<{ fil: MusikkFil }> => {
    const response = await api.post(`/musikk/${musikkId}/filer`, {
      navn,
      filType,
      url,
    });
    return response.data;
  },

  deleteFile: async (musikkId: number, filId: number): Promise<void> => {
    await api.delete(`/musikk/${musikkId}/filer/${filId}`);
  },

  getFileUrl: async (musikkId: number, filId: number): Promise<{ url: string }> => {
    const response = await api.get(`/musikk/${musikkId}/filer/${filId}/url`);
    return response.data;
  },
};

